import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { fontMerriweather } from "@product/scmp-sdk";

import { SubHeadline } from "~/components/article/article-render/generic-article/styles";
import Placeholder from "~/components/author/author-avatar/placeholder.svg";
import { Tooltip, tooltipClasses } from "~/components/common/tooltip";
import { ContentSchemaRender } from "~/components/content/content-schema-render";
import { setFullViewportWidthStyles } from "~/lib/styles";

import OpinionIcon from "./opinion.svg";

export type OpinionArticleLayoutProps = {
  $isColumnistArticle: boolean;
};

export const Container = styled.div<OpinionArticleLayoutProps>`
  ${props => {
    if (!props.$isColumnistArticle) return;
    return css`
      ${props.theme.breakpoints.up("desktop")} {
        display: flex;
        flex-direction: row;

        ${HeaderContainer} {
          position: relative;

          display: flex;
          flex-direction: column;
          justify-content: center;

          max-inline-size: 50%;
          margin-block-start: 0;
          padding-block-start: 40px;
          padding-block-end: 50px;
          padding-inline-start: 160px;
          padding-inline-end: 60px;

          border-width: 8px;

          ${OpinionAuthorHeadContainer} {
            position: absolute;
            inset-block-start: 50%;
            inset-inline-start: 0;

            display: flex;
            flex-direction: column;

            inline-size: 100%;
            max-inline-size: 250px;
            margin-inline-start: -125px;
            padding-block: 34px;
            padding-inline: 28px;

            color: #ffffff;
            font-size: 24px;

            background-color: #000000;

            transform: translateY(-50%);

            ${AuthorHeaderContent} {
              display: block;

              margin-block-start: 12px;
            }
          }

          ${OpinionAuthorBlockDesktopBlock} {
            display: none;
          }

          ${Author} {
            margin-block-start: 10px;
          }
        }
      }
    `;
  }}
`;

export const OpinionAuthorHeadContainer = styled.div`
  grid-area: OpinionAuthorHeadContainer;

  display: flex;
  flex-direction: row;

  margin-block-start: 16px;
  padding-inline: 14px;

  ${props => props.theme.breakpoints.up("tablet")} {
    flex-direction: column;

    margin-block-start: 0;
    padding-inline: 0;
  }
`;

export const HeaderContainer = styled.div`
  position: relative;
  z-index: 1;

  display: grid;
  grid:
    "OpinionAuthorHeadContainer" min-content
    "headline" min-content
    "sub-headline" min-content
    "." min-content
    "opinion-author" min-content;

  margin-block-start: -30px;

  background-color: #ffffff;

  box-shadow: inset 0 0 0 2px #34b8b5;

  ${props => props.theme.breakpoints.up("tablet")} {
    grid:
      "OpinionAuthorHeadContainer . headline" min-content
      ". . sub-headline" min-content
      ". . opinion-author" min-content
      / min-content 60px auto;

    margin-block-start: 30px;
    padding: 36px;

    border: 4px solid #34b8b5;

    box-shadow: none;

    > div {
      padding-inline: 0;
    }
  }
`;

export const ColumnistProfilePictureContainer = styled.figure`
  ${setFullViewportWidthStyles()}

  ${props => props.theme.breakpoints.up("tablet")} {
    display: none;
  }

  ${props => props.theme.breakpoints.up("desktop")} {
    inset-inline-start: 0;
    inset-inline-end: 0;

    display: block;

    inline-size: 100%;
    margin-inline: 0;
  }
`;

export const StyledHeadline = styled.div`
  grid-area: headline;

  padding-inline: 2px;
`;

export const StyledSubHeadline = styled(SubHeadline)`
  padding-block-end: 24px;
  padding-inline: 14px;

  ${props => props.theme.breakpoints.up("tablet")} {
    padding-block-end: 0;
  }
`;

export const StyledOpinionIcon = styled(OpinionIcon)`
  inline-size: 30px;
  block-size: 29px;

  ${props => props.theme.breakpoints.up("tablet")} {
    inline-size: 70px;
    block-size: 69px;
  }
`;

export const AuthorHeadContentContainer = styled.div`
  margin-inline-start: 12px;

  font-size: 14px;
  font-family: ${fontMerriweather};
  font-style: italic;
  line-height: 20px;

  ${props => props.theme.breakpoints.up("tablet")} {
    margin-inline-start: 0;
  }
`;

export const Label = styled.div`
  font-weight: 900;
  font-size: 18px;

  ${props => props.theme.breakpoints.up("tablet")} {
    margin-block-start: 12px;

    font-size: 24px;
    line-height: 32px;
  }
`;

export const AuthorHeaderContent = styled.div`
  display: inline;

  ${props => props.theme.breakpoints.up("tablet")} {
    display: none;
  }
`;

export const OpinionAuthorBlockDesktopBlock = styled.div`
  display: none;
  ${props => props.theme.breakpoints.up("tablet")} {
    grid-area: author-heading;

    display: inline;

    font-family: ${fontMerriweather};
    font-style: italic;
  }
`;

export const TypeName = styled.div`
  display: inline;

  font-weight: 900;

  ${props => props.theme.breakpoints.up("tablet")} {
    font-size: 20px;
  }
`;

export const AuthorNames = styled.div`
  display: inline;

  font-weight: 300;
  font-size: 14px;
  ${props => props.theme.breakpoints.up("desktop")} {
    font-size: 20px;
  }
`;

export const Author = styled.div`
  grid-area: opinion-author;

  z-index: 1;

  padding-block-start: 20px;

  background-color: #ffffff;
  border-block-start: 2px solid #34b8b5;

  ${props => props.theme.breakpoints.up("tablet")} {
    border-block-start: none;
  }
`;

export const StyledTooltip = styled(Tooltip)`
  & .${tooltipClasses.tooltip} {
    inline-size: 300px;
    padding: 20px;

    color: #222222;

    border: 1px solid #cccccc;
    border-radius: 4px;

    background-color: #f6f6f6;
  }

  & .${tooltipClasses.arrow} {
    color: #f6f6f6;
    font-size: 20px;
    &::before {
      border: 1px solid #cccccc;
    }
  }
`;
export const TooltipHeading = styled.span`
  font-weight: 800;
`;
export const TooltipContainer = styled.div`
  font-size: 14px;
  p {
    display: inline;
  }
`;

export const StyledContentSchemaRender = styled(ContentSchemaRender)`
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
`;

export const StyledPlaceholder = styled(Placeholder)`
  inline-size: 100%;
  block-size: 100%;

  background: #d7d7d7;

  object-fit: cover;
  object-position: 50% 50%;
`;
